import React, { useEffect, useRef, useState } from 'react'
import type { Task } from '../../types/mockup'

interface DependencyLinesProps {
  tasks: Task[]
  containerRef?: React.RefObject<HTMLElement>
}

interface Connection {
  fromId: string
  toId: string
  fromY: number
  toY: number
  color: string
}

export const DependencyLines: React.FC<DependencyLinesProps> = ({ 
  tasks, 
  containerRef 
}) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const [connections, setConnections] = useState<Connection[]>([])
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const calculateConnections = () => {
      if (!containerRef?.current) return

      const container = containerRef.current
      const containerRect = container.getBoundingClientRect()
      const newConnections: Connection[] = []

      setDimensions({
        width: containerRect.width,
        height: container.scrollHeight
      })

      tasks.forEach(task => {
        if (task.dependencies && task.dependencies.length > 0) {
          task.dependencies.forEach(depId => {
            const depTask = tasks.find(t => t.id === depId)
            if (!depTask) return

            const fromElement = container.querySelector(`[data-task-id="${depId}"]`)
            const toElement = container.querySelector(`[data-task-id="${task.id}"]`)

            if (fromElement && toElement) {
              const fromRect = fromElement.getBoundingClientRect()
              const toRect = toElement.getBoundingClientRect()
              const scrollTop = container.scrollTop

              const fromY = fromRect.top - containerRect.top + fromRect.height / 2 + scrollTop
              const toY = toRect.top - containerRect.top + toRect.height / 2 + scrollTop

              let color = '#95A5A6'
              if (depTask.status === 'complete') {
                color = '#10b981'
              } else if (depTask.status === 'delayed' || depTask.status === 'at_risk') {
                color = '#ef4444'
              } else if (depTask.status === 'in_progress') {
                color = '#f59e0b'
              }

              newConnections.push({
                fromId: depId,
                toId: task.id,
                fromY,
                toY,
                color
              })
            }
          })
        }
      })

      setConnections(newConnections)
    }

    calculateConnections()

    const resizeObserver = new ResizeObserver(calculateConnections)
    if (containerRef?.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [tasks, containerRef])

  const drawPath = (connection: Connection): string => {
    const startX = 20
    const endX = dimensions.width - 40
    const midX = (startX + endX) / 2
    
    const controlOffset = Math.min(Math.abs(connection.toY - connection.fromY) / 4, 50)
    
    return `
      M ${startX} ${connection.fromY}
      C ${startX + controlOffset} ${connection.fromY},
        ${midX} ${connection.fromY},
        ${midX} ${(connection.fromY + connection.toY) / 2}
      S ${midX} ${connection.toY},
        ${endX} ${connection.toY}
    `
  }

  const drawArrow = (connection: Connection) => {
    const endX = dimensions.width - 40
    const arrowSize = 8
    
    return (
      <g>
        <polygon
          points={`
            ${endX},${connection.toY}
            ${endX - arrowSize},${connection.toY - arrowSize / 2}
            ${endX - arrowSize},${connection.toY + arrowSize / 2}
          `}
          fill={connection.color}
        />
      </g>
    )
  }

  if (connections.length === 0) return null

  return (
    <svg
      ref={svgRef}
      className="dependency-lines-svg"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: dimensions.width,
        height: dimensions.height,
        pointerEvents: 'none',
        zIndex: 1
      }}
    >
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="2" dy="2" stdDeviation="2" floodOpacity="0.1" />
        </filter>
      </defs>
      {connections.map((connection, index) => (
        <g key={`${connection.fromId}-${connection.toId}-${index}`}>
          <path
            d={drawPath(connection)}
            stroke={connection.color}
            strokeWidth="2"
            fill="none"
            strokeDasharray={connection.color === '#ef4444' ? '5,5' : undefined}
            opacity="0.7"
            filter="url(#shadow)"
          />
          {drawArrow(connection)}
          <circle
            cx={20}
            cy={connection.fromY}
            r="4"
            fill={connection.color}
          />
        </g>
      ))}
    </svg>
  )
}

export default DependencyLines